import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  supportedLanguages, 
  languageNames, 
  languageFlags,
  SupportedLanguage 
} from '@/i18n/config';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Globe } from 'lucide-react';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'flags';
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'dropdown',
  className = '' 
}) => {
  const { currentLanguage, setLanguage } = useLanguage();

  if (variant === 'flags') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {supportedLanguages.map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            className={`text-xl p-1 rounded transition-all duration-200 hover:scale-110 ${
              currentLanguage === lang 
                ? 'bg-white/10 ring-2 ring-indigo-500' 
                : 'opacity-60 hover:opacity-100'
            }`}
            aria-label={`Switch to ${languageNames[lang]}`}
          >
            {languageFlags[lang]}
          </button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`text-white/60 hover:text-white hover:bg-white/[0.05] gap-2 ${className}`}
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline">{languageFlags[currentLanguage]}</span>
          <span className="text-xs">{currentLanguage.toUpperCase()}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="bg-[#0a0a0a] border-white/10 min-w-[140px]"
      >
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onClick={() => setLanguage(lang)}
            className={`cursor-pointer gap-2 text-white/80 hover:text-white hover:bg-white/[0.05] ${
              currentLanguage === lang ? 'bg-white/[0.05] text-white' : ''
            }`}
          >
            <span>{languageFlags[lang]}</span>
            <span>{languageNames[lang]}</span>
            {currentLanguage === lang && (
              <span className="ml-auto text-indigo-400">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
